import { pdfjs } from "react-pdf"
import type { CellGeom } from "../components/BookPageOverlay"

export interface DetectedCell extends CellGeom { label?: string; section?: string }

type Mat = [number, number, number, number, number, number]
const IDENT: Mat = [1, 0, 0, 1, 0, 0]
const mul = (m: Mat, n: Mat): Mat => [
  m[0] * n[0] + m[2] * n[1], m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3], m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4], m[1] * n[4] + m[3] * n[5] + m[5],
]
const apply = (m: Mat, x: number, y: number): [number, number] => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]]

type TextItem = { str: string; transform: number[]; width: number; height: number }
type Rect = [number, number, number, number] // minX,minY,maxX,maxY (device space)

// Extract axis-aligned rectangles from a pdf.js path-data array (DrawOPS:
// moveTo=0, lineTo=1, curveTo=2, closePath=3). Recovers individual rectangles
// even when several are batched into one fill.
function rectsFromPath(data: ArrayLike<number>, ctm: Mat, out: Rect[]) {
  let pts: [number, number][] = []
  let hasCurve = false
  const flush = () => {
    if (!hasCurve && pts.length >= 4) {
      const xs = new Set(pts.map((p) => Math.round(p[0] * 2)))
      const ys = new Set(pts.map((p) => Math.round(p[1] * 2)))
      if (xs.size <= 2 && ys.size <= 2) {
        const xmin = Math.min(...pts.map((p) => p[0])), xmax = Math.max(...pts.map((p) => p[0]))
        const ymin = Math.min(...pts.map((p) => p[1])), ymax = Math.max(...pts.map((p) => p[1]))
        if (xmax - xmin > 0.5 && ymax - ymin > 0.5) {
          const a = apply(ctm, xmin, ymin), b = apply(ctm, xmax, ymax)
          out.push([Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[0], b[0]), Math.max(a[1], b[1])])
        }
      }
    }
    pts = []; hasCurve = false
  }
  let i = 0
  const n = data.length
  while (i < n) {
    const op = data[i++]
    if (op === 0) { flush(); pts.push([data[i++], data[i++]]) }
    else if (op === 1) { pts.push([data[i++], data[i++]]) }
    else if (op === 2) { hasCurve = true; i += 6 }
    else if (op === 3) { /* closePath */ }
    else break
  }
  flush()
}

/**
 * Analyse a page: detect filled cell-sized rectangles and attach the row label
 * (text to the left, same band). Heuristic — meant to be reviewed/edited.
 */
export async function detectFields(pdf: unknown, pageNumber: number): Promise<DetectedCell[]> {
  const doc = pdf as { getPage: (n: number) => Promise<{
    getViewport: (o: { scale: number }) => { width: number; height: number }
    getOperatorList: () => Promise<{ fnArray: number[]; argsArray: unknown[] }>
    getTextContent: () => Promise<{ items: TextItem[] }>
  }> }
  const page = await doc.getPage(pageNumber)
  const vp = page.getViewport({ scale: 1 })
  const W = vp.width, H = vp.height
  const OPS = pdfjs.OPS as Record<string, number>
  const FILLS = new Set<number>([OPS.fill, OPS.eoFill, OPS.fillStroke, OPS.eoFillStroke, OPS.closeFillStroke, OPS.closeEOFillStroke])

  const { fnArray, argsArray } = await page.getOperatorList()
  let ctm: Mat = [...IDENT]
  const stack: Mat[] = []
  const raw: Rect[] = []
  for (let i = 0; i < fnArray.length; i++) {
    const fn = fnArray[i]
    const args = argsArray[i]
    if (fn === OPS.save) stack.push([...ctm])
    else if (fn === OPS.restore) ctm = stack.pop() ?? [...IDENT]
    else if (fn === OPS.transform) ctm = mul(ctm, args as unknown as Mat)
    else if (fn === OPS.constructPath) {
      const a = args as unknown[]
      const paintOp = a[0] as number
      if (!FILLS.has(paintOp)) continue
      const data = (a[1] as unknown[])?.[0] as ArrayLike<number> | undefined
      if (data && typeof data.length === "number") rectsFromPath(data, ctm, raw)
    }
  }

  // Normalise (top-left origin), keep cell-shaped rects (wide & short → drops
  // colored headers/logos/notes), dedupe.
  const seen = new Set<string>()
  let cells: DetectedCell[] = []
  for (const [minX, minY, maxX, maxY] of raw) {
    const x = minX / W, w = (maxX - minX) / W
    const h = (maxY - minY) / H, y = 1 - maxY / H
    if (w < 0.012 || w > 0.45 || h < 0.006 || h > 0.045) continue
    if (w / h < 1.2) continue // cells are wider than tall; skips square headers
    if (x < -0.02 || x > 1.02 || y < -0.02 || y > 1.02) continue
    const key = `${Math.round(x * 500)},${Math.round(y * 500)},${Math.round(w * 500)},${Math.round(h * 500)}`
    if (seen.has(key)) continue
    seen.add(key)
    cells.push({ x: Math.round(x * 1e5) / 1e5, y: Math.round(y * 1e5) / 1e5, w: Math.round(w * 1e5) / 1e5, h: Math.round(h * 1e5) / 1e5 })
  }
  cells.sort((a, b) => (Math.abs(a.y - b.y) > 0.01 ? a.y - b.y : a.x - b.x))
  if (cells.length === 0) return cells

  // Row labels from text to the left of each row band.
  const tc = await page.getTextContent()
  const words = tc.items
    .filter((it) => it.str && it.str.trim())
    .map((it) => ({ str: it.str.trim(), x: it.transform[4] / W, w: (it.width || 0) / W, yc: 1 - (it.transform[5] + (it.height || 0) * 0.5) / H }))

  const rows: DetectedCell[][] = []
  for (const c of cells) {
    const cyc = c.y + c.h / 2
    const row = rows.find((r) => Math.abs((r[0].y + r[0].h / 2) - cyc) < Math.max(0.01, c.h * 0.8))
    if (row) row.push(c); else rows.push([c])
  }
  for (const row of rows) {
    const band = row[0].y + row[0].h / 2
    const leftMost = Math.min(...row.map((c) => c.x))
    const label = words
      .filter((w) => w.x + w.w <= leftMost - 0.005 && Math.abs(w.yc - band) < Math.max(0.014, row[0].h))
      .sort((a, b) => a.x - b.x)
      .map((w) => w.str)
      .join(" ").replace(/\s+/g, " ").trim().slice(0, 120)
    if (label) for (const c of row) c.label = label
  }
  return cells
}
